import React, { useState } from "react";
import {
  Shield,
  ShieldCheck,
  FileText,
  AlertTriangle,
  Atom,
  Search,
  Plus,
  MoreVertical,
  CheckCircle2,
  FileCode,
  Download,
  Layers,
  Edit,
  Power,
  ChevronDown,
  Info
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell
} from "recharts";

// --- Mock Data ---

const complianceTrendData = [
  { date: "May 25", score: 85 },
  { date: "May 26", score: 88.5 },
  { date: "May 27", score: 88.5 },
  { date: "May 28", score: 92 },
  { date: "May 29", score: 95 },
  { date: "May 30", score: 96 },
  { date: "May 31", score: 98 },
];

const categoryPieData = [
  { name: "Cryptographic", value: 30, color: "#10B981" },
  { name: "Signing", value: 25, color: "#3B82F6" },
  { name: "Certificate", value: 20, color: "#8B5CF6" },
  { name: "Access Control", value: 15, color: "#EC4899" },
  { name: "AI Security", value: 10, color: "#06B6D4" },
];

const policiesList = [
  {
    id: 1,
    name: "Quantum Signature Policy",
    category: "Signing",
    catBg: "bg-emerald-100 text-emerald-800",
    priority: "High",
    prioBg: "bg-red-50 text-red-600",
    status: "Active",
    statusBg: "bg-emerald-100 text-emerald-700",
    compliance: "100%",
    updated: "May 30, 2026",
  },
  {
    id: 2,
    name: "Certificate Management Policy",
    category: "Certificate",
    catBg: "bg-blue-100 text-blue-800",
    priority: "High",
    prioBg: "bg-red-50 text-red-600",
    status: "Active",
    statusBg: "bg-emerald-100 text-emerald-700",
    compliance: "98%",
    updated: "May 29, 2026",
  },
  {
    id: 3,
    name: "Artifact Signing Policy",
    category: "Cryptographic",
    catBg: "bg-purple-100 text-purple-800",
    priority: "Medium",
    prioBg: "bg-amber-50 text-amber-600",
    status: "Active",
    statusBg: "bg-emerald-100 text-emerald-700",
    compliance: "97%",
    updated: "May 28, 2026",
  },
  {
    id: 4,
    name: "AI Security Policy",
    category: "AI Security",
    catBg: "bg-cyan-100 text-cyan-800",
    priority: "Medium",
    prioBg: "bg-amber-50 text-amber-600",
    status: "Active",
    statusBg: "bg-emerald-100 text-emerald-700",
    compliance: "96%",
    updated: "May 27, 2026",
  },
  {
    id: 5,
    name: "Access Control Policy",
    category: "Access Control",
    catBg: "bg-emerald-100 text-emerald-800",
    priority: "Low",
    prioBg: "bg-emerald-50 text-emerald-600",
    status: "Active",
    statusBg: "bg-emerald-100 text-emerald-700",
    compliance: "100%",
    updated: "May 26, 2026",
  },
];

const violationsData = [
  { label: "Weak Algorithm", pct: "40%", width: "40%", color: "bg-red-500" },
  { label: "Expired Certificate", pct: "25%", width: "25%", color: "bg-orange-400" },
  { label: "Unsigned Artifact", pct: "20%", width: "20%", color: "bg-amber-400" },
  { label: "Access Control", pct: "15%", width: "15%", color: "bg-emerald-500" },
];

const riskSystems = [
  { system: "Firmware / IoT", risk: "High", riskBg: "bg-red-50 text-red-600", status: "Monitoring", statusBg: "bg-amber-100 text-amber-700" },
  { system: "AI Models", risk: "Medium", riskBg: "bg-amber-50 text-amber-600", status: "Protected", statusBg: "bg-emerald-100 text-emerald-700" },
  { system: "Code Repository", risk: "Medium", riskBg: "bg-amber-50 text-amber-600", status: "Protected", statusBg: "bg-emerald-100 text-emerald-700" },
  { system: "APIs", risk: "Low", riskBg: "bg-emerald-50 text-emerald-600", status: "Protected", statusBg: "bg-emerald-100 text-emerald-700" },
];

export default function TrustPolicies() {
  const [selectedPolicy, setSelectedPolicy] = useState(policiesList[0]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-1 text-slate-800 font-sans space-y-5 text-xs">
      
      {/* ================= HEADER BANNER ================= */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-3 text-white flex flex-col md:flex-row justify-between items-start md:items-center shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-md">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Trust Policies</h1>
            <p className="text-blue-100 text-xs mt-0.5">Define, enforce and monitor quantum-safe security policies</p>
          </div>
        </div>

        <div className="flex items-center gap-6 mt-4 md:mt-0 divide-x divide-white/20">
          <div className="flex items-center gap-2 pl-4">
            <Shield className="w-4 h-4 text-blue-200" />
            <div>
              <div className="text-sm font-extrabold">12</div>
              <div className="text-[10px] text-blue-100">Active Policies</div>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-6">
            <div>
              <div className="text-sm font-extrabold">98%</div>
              <div className="text-[10px] text-blue-100">Compliance</div>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-6">
            <AlertTriangle className="w-4 h-4 text-red-300" />
            <div>
              <div className="text-sm font-extrabold">3</div>
              <div className="text-[10px] text-blue-100">Policy Alerts</div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= TOP METRIC CARDS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Card 1 */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 font-medium">Policy Compliance</div>
            <div className="text-lg font-bold text-slate-900">98%</div>
            <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">▲ 8% vs. last 7 days</div>
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 rounded-lg text-blue-600">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 font-medium">Signed Documents</div>
            <div className="text-lg font-bold text-slate-900">1,248</div>
            <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">▲ 12% vs. last 7 days</div>
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 bg-red-50 rounded-lg text-red-500">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 font-medium">Policy Violations</div>
            <div className="text-lg font-bold text-slate-900">3</div>
            <div className="text-[10px] text-red-500 font-semibold mt-0.5">▼ 40% vs. last 7 days</div>
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 bg-purple-50 rounded-lg text-purple-600">
            <Atom className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 font-medium">Quantum-Safe Status</div>
            <div className="text-lg font-bold text-slate-900">100%</div>
            <div className="text-[10px] text-emerald-600 font-medium mt-0.5">All critical systems compliant</div>
          </div>
        </div>
      </div>

      {/* ================= MIDDLE CHARTS & SIDEBAR ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        
        {/* Left 8 columns: 3 Top Analytics Panels */}
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-12 gap-4">
          
          {/* Policy Compliance Trend */}
          <div className="md:col-span-5 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-slate-900 text-xs">Policy Compliance Trend</h3>
              <span className="text-emerald-600 bg-emerald-50 font-bold px-1.5 py-0.5 rounded text-[10px]">▲ 6%</span>
            </div>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={complianceTrendData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                  <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={9} domain={[80, 100]} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#8B5CF6" strokeWidth={2} dot={{ fill: "#8B5CF6", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Policy Category Donut */}
          <div className="md:col-span-4 bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-900 text-xs mb-2">Policy Category</h3>
            <div className="flex items-center gap-2">
              <div className="relative w-24 h-24 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={categoryPieData} innerRadius={24} outerRadius={38} paddingAngle={2} dataKey="value">
                      {categoryPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-sm font-black text-slate-900 leading-none">12</span>
                  <span className="text-[8px] text-slate-400 font-medium">Total</span>
                </div>
              </div>

              <div className="space-y-1 text-[9px] w-full">
                {categoryPieData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-slate-600 truncate">{item.name}</span>
                    </div>
                    <span className="font-bold text-slate-800">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Alerts */}
          <div className="md:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold text-slate-900 text-xs">Recent Alerts</h3>
                <button className="text-blue-600 font-semibold text-[10px] hover:underline">View All &rarr;</button>
              </div>

              <div className="space-y-2.5 text-[10px]">
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-800 leading-tight">Firmware signing policy violation detected</div>
                    <div className="text-slate-400 text-[9px]">May 30, 14:32</div>
                  </div>
                </div>

                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-800 leading-tight">Weak certificate in use</div>
                    <div className="text-slate-400 text-[9px]">May 29, 11:20</div>
                  </div>
                </div>

                <div className="flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-800 leading-tight">Policy updated successfully</div>
                    <div className="text-slate-400 text-[9px]">May 28, 09:14</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TABLE PANEL: Trust Policies Data Table */}
          <div className="md:col-span-12 bg-white rounded-xl p-5 border border-slate-200 shadow-sm space-y-4">
            
            {/* Filter Bar */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3">
              <h3 className="font-bold text-slate-900 text-sm">Trust Policies</h3>
              
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 md:w-52">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search policies..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <button className="flex items-center gap-1 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-600 text-[11px] font-medium">
                  Category: <span className="font-bold text-slate-800">All</span> <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>

                <button className="flex items-center gap-1 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-600 text-[11px] font-medium">
                  Status: <span className="font-bold text-slate-800">All</span> <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>

                <button className="flex items-center gap-1 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-600 text-[11px] font-medium">
                  Risk: <span className="font-bold text-slate-800">All</span> <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>

                <button className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded-lg text-[11px]">
                  <Plus className="w-3.5 h-3.5" /> New Policy
                </button>
              </div>
            </div>

            {/* Main Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="text-slate-400 font-semibold border-b border-slate-100">
                    <th className="pb-3 font-medium">Policy Name</th>
                    <th className="pb-3 font-medium">Category</th>
                    <th className="pb-3 font-medium">Priority</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Compliance</th>
                    <th className="pb-3 font-medium">Last Updated</th>
                    <th className="pb-3 font-medium text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {policiesList.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedPolicy(row)}
                      className={`cursor-pointer transition-colors hover:bg-slate-50/80 ${selectedPolicy.id === row.id ? "bg-slate-50/80" : ""}`}
                    >
                      <td className="py-3 font-bold text-blue-600 flex items-center gap-1.5">
                        {row.name}
                        {selectedPolicy.id === row.id && <span className="w-1.5 h-1.5 rounded-full bg-blue-600 inline-block"/>}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${row.catBg}`}>
                          {row.category}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${row.prioBg}`}>
                          {row.priority}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${row.statusBg}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-3 font-bold text-emerald-600">{row.compliance}</td>
                      <td className="py-3 text-slate-500 font-medium">{row.updated}</td>
                      <td className="py-3 text-center text-slate-400 hover:text-slate-600">
                        <MoreVertical className="w-4 h-4 mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>

        </div>

        {/* Right 4 columns: Policy Details Sidebar */}
        <div className="lg:col-span-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
          <div>
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 mb-4">
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-blue-600" />
                <h3 className="font-bold text-slate-900 text-xs">Policy Details</h3>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <h2 className="text-sm font-bold text-slate-900">{selectedPolicy.name}</h2>
                <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Active
                </span>
              </div>

              <p className="text-[11px] text-slate-500 leading-relaxed">
                Enforces post-quantum digital signature standards and key usage.
              </p>

              {/* Specs Grid */}
              <div className="space-y-2 text-[11px] pt-1">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium">Category</span>
                  <span className="font-semibold text-slate-800">Signing / Cryptographic</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium">Priority</span>
                  <span className="font-bold text-red-600">High</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium">Compliance</span>
                  <span className="font-semibold text-slate-800">NIST PQC, FIPS 140-3, eIDAS</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium">Created</span>
                  <span className="font-medium text-slate-700">May 20, 2026</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium">Last Updated</span>
                  <span className="font-medium text-slate-700">{selectedPolicy.updated}</span>
                </div>
              </div>

              {/* Policy Rules List */}
              <div className="pt-2">
                <h4 className="font-bold text-slate-900 text-[11px] mb-2.5">Policy Rules</h4>
                <div className="space-y-2 text-[11px]">
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Use PQC algorithms (ML-DSA / Dilithium3)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Reject SHA-1, SHA-2 for long-term signatures</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Enforce hybrid (Classical + PQ) signatures</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Minimum key length: 256-bit security</span>
                  </div>
                </div>
              </div>

              {/* Scope Pills */}
              <div className="pt-2">
                <h4 className="font-bold text-slate-900 text-[11px] mb-2">Scope</h4>
                <div className="flex flex-wrap gap-1.5">
                  <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1">
                    <FileText className="w-3 h-3 text-slate-500" /> Documents
                  </span>
                  <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1">
                    <FileCode className="w-3 h-3 text-slate-500" /> Software / Firmware
                  </span>
                  <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1">
                    <Atom className="w-3 h-3 text-slate-500" /> APIs
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-3 border-t border-slate-100">
            <button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg text-[11px] flex items-center justify-center gap-1">
              <Edit className="w-3.5 h-3.5" /> Edit Policy
            </button>
            <button className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold py-2 rounded-lg text-[11px] flex items-center justify-center gap-1">
              <Power className="w-3.5 h-3.5 text-slate-500" /> Disable Policy
            </button>
          </div>
        </div>

      </div>

      {/* ================= BOTTOM PANELS ROW ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        
        {/* Policy Violations by Type */}
        <div className="lg:col-span-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-900 text-xs mb-3">Policy Violations by Type</h3>
          <div className="space-y-3">
            {violationsData.map((item) => (
              <div key={item.label} className="space-y-1">
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-700 font-medium">{item.label}</span>
                  <span className="font-bold text-slate-900">{item.pct}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div className={`h-full rounded-full ${item.color}`} style={{ width: item.width }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Risk Systems */}
        <div className="lg:col-span-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-900 text-xs mb-3">Top Risk Systems</h3>
          <table className="w-full text-left text-[11px]">
            <thead>
              <tr className="text-slate-400 font-medium border-b border-slate-100">
                <th className="pb-2 font-medium">System</th>
                <th className="pb-2 font-medium">Risk</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {riskSystems.map((item) => (
                <tr key={item.system}>
                  <td className="py-2 font-semibold text-slate-800">{item.system}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${item.riskBg}`}>{item.risk}</span>
                  </td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${item.statusBg}`}>{item.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-900 text-xs mb-3">Quick Actions</h3>
          <div className="grid grid-cols-3 gap-2">
            <button className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl p-3 flex flex-col items-center text-center transition-all">
              <ShieldCheck className="w-5 h-5 text-blue-600 mb-1" />
              <span className="font-semibold text-slate-800 text-[10px] leading-tight">Run Compliance Scan</span>
            </button>

            <button className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl p-3 flex flex-col items-center text-center transition-all">
              <FileText className="w-5 h-5 text-blue-600 mb-1" />
              <span className="font-semibold text-slate-800 text-[10px] leading-tight">View Compliance Logs</span>
            </button>

            <button className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl p-3 flex flex-col items-center text-center transition-all">
              <Download className="w-5 h-5 text-blue-600 mb-1" />
              <span className="font-semibold text-slate-800 text-[10px] leading-tight">Export Report</span>
            </button>

            <button className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl p-3 flex flex-col items-center text-center transition-all">
              <Layers className="w-5 h-5 text-indigo-600 mb-1" />
              <span className="font-semibold text-slate-800 text-[10px] leading-tight">Policy Templates</span>
            </button>
          </div>
        </div>

      </div>

    </div>
  );
}